import React from "react";

import { Title } from "~/components";
import { usePageTitle } from "~/shared";

export const PrivacyPolicy = () => {
  usePageTitle("Privacy Policy");

  return (
    <main className="min-h-screen bg-gray-50 py-4 md:py-8">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 text-center">
          <Title className="mb-2 text-2xl font-bold text-gray-900 md:text-3xl">
            Elektra Health Privacy Policy
          </Title>
          <p className="text-sm text-gray-500">
            <strong>Last modified:</strong> September 15, 2026
          </p>
        </div>

        <div className="space-y-8">
          {/* Introduction Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Introduction
            </h2>
            <p className="mb-4 text-gray-700">
              Your privacy matters to us. This Privacy Policy applies to My
              Elektra, Inc., a Delaware corporation ("Elektra Health," "we,"
              "us," or "our"). We provide certain administrative and management
              services to Electra Health Medical, P.C., a New York professional
              service corporation, Electra Health Medical Mass, P.C., a
              Massachusetts professional service corporation, Elektra Women's
              Health Medical, Inc., a Pennsylvania professional corporation,
              and/or Electra Women's Health Medical NJ, PC, a New Jersey
              professional service corporation (collectively "the Practice"), as
              well as provide access to a technology platform to facilitate the
              care you receive from the Practice.
            </p>
            <p className="mb-4 text-gray-700">
              It describes what personally identifiable information may be
              collected by or shared with us on www.elektrahealth.com and all
              related websites (collectively, the "Site"), mobile applications
              (the "Apps"), and web-based services (collectively with Site and
              Apps, our "Services"). It also describes how that information is
              stored, and how we use that information. Users ("Users") include
              Elektra patients, members, and Site visitors as well as providers.
            </p>
            <p className="mb-4 text-gray-700">
              For the purpose of this Privacy Policy, "Personally Identifiable
              Information" means any information that, by itself or in
              combination with other information, identifies or can reasonably
              be used to identify an individual, such as their name, email
              address, telephone number, address, or date of birth. Personally
              Identifiable Information does not include information that is
              anonymized, or publicly available information that has not been
              combined with non-public Personally Identifiable Information.
            </p>
            <p className="mb-4 text-gray-700">
              When you sign up for or use Elektra Health, we ask you to confirm
              that you've had the opportunity to review our Privacy Policy. This
              means that by submitting Personally Identifiable Information and
              other information to the Site, you consent to the collection, use,
              and disclosure of such information as set out in this Privacy
              Policy.
            </p>
            <p className="mb-4 text-gray-700">
              We may collect, store, and use personal data that is linked or
              reasonably linkable to you and that identifies your past, present,
              or future health status or mental health status, as may be
              applicable ("consumer health data"). If you are a resident of
              Connecticut, Nevada, or Washington, we provide information about
              consumer health data collected about you, as well as the rights
              you may have related to this data, in our{" "}
              <a
                href="/consent/consumer-health-data"
                className="text-blue-600 underline"
              >
                Consumer Health Data Privacy Notice
              </a>
              .
            </p>
            <p className="mb-4 text-gray-700">
              As described in more detail below, this Privacy Policy does not
              intend to include information about how we or the Practice handles
              HIPAA-regulated personal health information ("PHI").
            </p>
          </section>

          {/* Personal Information We Collect Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              1. Personal Information We Collect
            </h2>
            <p className="mb-4 text-gray-700">
              Generally, you may browse the Site and/or our applications without
              providing any Personally Identifiable Information. However, we ask
              that you provide information at various times and locations
              through the Site, through our Services, and/or through our
              electronic medical record.
            </p>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              1.1 Information We May Collect
            </h3>
            <p className="mb-4 text-gray-700">
              The following is a list of information that we may collect from or
              about you:
            </p>
            <ul className="mb-4 ml-6 list-disc text-gray-700">
              <li>
                Personally Identifying Information, including your name and
                contact information, such as your physical address, e-mail
                address and phone number
              </li>
              <li>Electronic signature</li>
              <li>
                Demographic information, including your gender, date of birth
                and zip code
              </li>
              <li>
                Billing and payment information, including billing address,
                health plan participation and enrollee information, and credit
                card information, such as account number, expiration date and
                security information
              </li>
              <li>Account information, including your username and password</li>
              <li>
                Device and / or browser information, including your IP address,
                operating system, connection speed, bandwidth, browser type, web
                page requests, cookie information, other hardware and software
                attributes
              </li>
              <li>Location information</li>
              <li>Usage activity and viewing preferences</li>
              <li>
                Photographic or video images submitted for identification
                purposes, including photographs of your driver's license or
                passport
              </li>
              <li>
                User information, including information or content you post or
                submit on the Services
              </li>
              <li>Transaction history</li>
              <li>
                For providers: your name and contact information and medical
                specialty
              </li>
              <li>
                Information we collect from or about you in other online or
                offline contexts, including information submitted during
                purchases of Services and interactions with our employees
              </li>
            </ul>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              1.2 Information When Requesting Appointments
            </h3>
            <p className="mb-4 text-gray-700">
              In addition, when you request an appointment with the Practice or
              from a physician or licensed practitioner associated with the
              Practice ("Practitioner" or "Practitioners"), you may share:
            </p>
            <ul className="mb-4 ml-6 list-disc text-gray-700">
              <li>
                Personal Information: your name and contact information and
                demographic information
              </li>
              <li>
                Protected health information, or PHI (defined below), including
                medical information submitted for diagnosis or treatment
                purposes (including information about your applicable medical
                history) and current complaint or other reason for visiting a
                Practitioner
              </li>
              <li>Date of visit</li>
              <li>
                Images or videos you share for diagnosis or treatment purposes
              </li>
              <li>Communications with the Practice or its Practitioners</li>
            </ul>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              1.3 Updating Your Information
            </h3>
            <p className="mb-4 text-gray-700">
              To update your personal account information or to opt out of
              emails, contact hello@elektrahealth.com.
            </p>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              1.4 Applicability of HIPAA
            </h3>
            <p className="mb-4 text-gray-700">
              The information you provide to the Practice or its Practitioners
              through the Services may be protected under federal and state laws
              applicable to health information, including but not limited to the
              Health Insurance Portability and Accountability Act of 1996,
              Public Law 104-191, and its implementing regulations, as may be
              amended from time to time (collectively, "HIPAA"). To the extent
              applicable, the Practice, its Practitioners, and pharmacies will
              use and disclose information about you that is protected under
              such privacy laws only as permitted or required by such laws. The
              Practice has adopted a{" "}
              <a
                href="/consent/privacy-practices"
                className="text-blue-600 underline"
              >
                Notice Of Privacy Practices
              </a>{" "}
              that describes how it uses and discloses individually identifiable
              health information protected by HIPAA. By accessing or using any
              part of the Services, you are acknowledging receipt of the Notice
              of Privacy Practices from the Practice.
            </p>
            <p className="mb-4 text-gray-700">
              As described in our Terms of Use, Elektra Health does not engage
              in the practice of medicine and is not a health care provider or a
              "covered entity" as defined by HIPAA. The Practice and its
              Practitioners each may be considered a "covered entity" under
              HIPAA, and Elektra Health may, in some cases, also be a "business
              associate" of a practice or its providers. If we are deemed a
              "business associate," we will be subject to additional protections
              with respect to your protected health information, as defined
              under HIPAA, or other state laws. Your PHI will be used and
              disclosed only in accordance with such applicable laws and
              regulations.
            </p>
            <p className="mb-4 text-gray-700">
              We will not use or disclose PHI or other information about you
              that is protected under health information privacy laws except on
              behalf of the Practice, its Practitioners and pharmacies, or as
              otherwise permitted by such laws. We also may de-identify PHI in
              compliance with HIPAA, and use such de-identified data for
              research or marketing purposes, or for any other purpose not
              prohibited by law.
            </p>
          </section>

          {/* How We Collect Personal Information Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              2. How We Collect Personal Information
            </h2>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              2.1 Direct Collection
            </h3>
            <p className="mb-4 text-gray-700">
              We may collect information directly from you when you visit,
              access or use the Services or when you register for an account
              with us. We may also collect information when you purchase a
              service, submit information to use through a survey, register for
              in-person or virtual educational or promotional events,
              communicate with us, or post or submit content on or in the
              Services.
            </p>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              2.2 From Practice and Third Parties
            </h3>
            <p className="mb-4 text-gray-700">
              In addition to the information we collect directly from you, we
              may also collect information from the Practice and/or its
              Practitioners who provide treatment and other services to you in
              connection with the Services. This information may include, but is
              not limited to, diagnoses, care and/or treatment plans, and notes.
              We also may receive information from third parties that pay for
              your care or provide you with treatment or prescription
              medication, which may include prescription history, insurance
              policy, insurance eligibility and coverage, and laboratory or
              other testing results.
            </p>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              2.3 Browser and Device Information
            </h3>
            <p className="mb-4 text-gray-700">
              Some of the information we collect depends on the settings on your
              web browser or mobile device. Please check your web browser or
              mobile device if you want to learn what information your browser
              or mobile device sends or how to change your settings. In addition
              to the information sent by your web browser or mobile device, as
              you navigate through a website or mobile app, certain information
              can be passively collected (that is, gathered without you actively
              providing the information) using various technologies and means,
              such as navigational data collection.
            </p>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              2.4 Cookies and Tracking Technologies
            </h3>
            <p className="mb-4 text-gray-700">
              Like most website operators, we use cookies and similar
              technologies, local shared objects (or any other successor tracker
              technology), Software Development Kits, and pixel tags that
              automatically receive and track certain data about how you and
              other visitors interact with our Services, preferences expressed
              and settings chosen. For example, we may collect information about
              the technology you use (such as your browser, type of computer,
              operating systems, mobile device and network used, and Internet
              service providers) and server log data (such as access dates and
              times, online features or pages viewed, system activity, and the
              third-party site or service you were using before interacting with
              our Services). These technologies are described in more detail
              below.
            </p>
            <div className="mb-4 space-y-3 text-gray-700">
              <p>
                <strong>a.) "Cookies"</strong> are small text files that are
                stored on your browser or device by websites, mobile apps,
                online media and advertisements. Cookies served by the entity
                that operates the website or app you are visiting or using are
                called <strong>"first party cookies"</strong>. Cookies served by
                companies that are not operating the website or app you are
                visiting or using are called{" "}
                <strong>"third party cookies"</strong>.{" "}
                <strong>"Session Cookies"</strong> last only as long as your
                browser or app is open and are deleted automatically once you
                close your browser or app. Other cookies are{" "}
                <strong>"persistent cookies"</strong> meaning that they survive
                after your browser or app is closed (for example, they may
                recognize your computer when you re-open our online services).
              </p>
              <p>
                <strong>b.) "Local shared objects"</strong> (also called Flash
                cookies or HTML5 cookies) and any other successor technology
                refers generally to the collection of cookie-like data stored on
                a browser or computer by websites, ads, or third parties.
              </p>
              <p>
                <strong>c.) "Pixel tags"</strong> (also called beacons or
                pixels) are small blocks of code installed on (or called by) a
                web page, app or advertisement which can retrieve certain
                information about your device and browser, including device
                type, operating system, browser type and version, website
                visited, time of visit, referring website, IP address, and other
                similar information.{" "}
                <strong>"Software Development Kits"</strong> (also called SDKs)
                function like pixels and cookies, but operate in the mobile app
                context where pixels and cookies cannot always function. The
                primary app developer can install pieces of code (the SDK) from
                partners in the app, thereby allowing such partners to collect
                certain information about user interaction with the app,
                information about the user device and network information.
              </p>
              <p>
                <strong>d.)</strong> We also use services such as Google
                Analytics, a web analysis service provided by Google, Inc.
                ("Google") to track website activity, along with the information
                on the traffic source. You can find out more about how Google
                uses data when you visit the Site and / or Apps by visiting
                google.com/policies/privacy/partners.
              </p>
            </div>
          </section>

          {/* How We May Use Personal Information Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              3. How We May Use Personal Information
            </h2>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              3.1 Purposes for Using Your Information
            </h3>
            <p className="mb-4 text-gray-700">
              We will use your information only for the following purposes:
            </p>
            <ul className="mb-4 ml-6 list-disc text-gray-700">
              <li>
                To provide, improve and create new services by Elektra Health
              </li>
              <li>
                To respond to your inquiries and to send you administrative
                communications about the Site
              </li>
              <li>
                To detect and defend against fraud and other threats to the Site
                and our users, and to administer your account
              </li>
              <li>
                To obtain your feedback about the Site and Elektra Health's
                services
              </li>
              <li>
                To send you electronic messages and emails, including news,
                announcements, reminders and opportunities from Elektra Health
              </li>
              <li>
                To process, fulfill, support, and administer transactions and
                orders for products and services ordered by you
              </li>
              <li>To contact you in response to a request</li>
              <li>
                To protect, establish or exercise our legal rights or defend
                against legal claims
              </li>
              <li>To administer quizzes, surveys, and sweepstakes</li>
              <li>To fulfill any other purpose for which you provide it</li>
              <li>
                To carry out our obligations and enforce our rights arising from
                any contracts entered into between you and us, including for
                billing and collection
              </li>
              <li>
                To notify you about changes to our Site, or any products or
                services we offer or provide though them
              </li>
              <li>
                In any other way we may describe when you provide the
                information
              </li>
              <li>For any other purpose with your consent</li>
            </ul>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              3.2 Information Sharing
            </h3>
            <p className="mb-4 text-gray-700">
              Subject to the limitations described in the Applicability of HIPAA
              section above, we may share the information we collect from you
              with the following third parties:
            </p>
            <ul className="mb-4 ml-6 list-disc text-gray-700">
              <li>
                Our third party service providers ("Vendors") that provide
                services to us to enable us to provide the Services, such as the
                hosting of the Services, data analysis, IT services and
                infrastructure, customer service, email delivery, and other
                similar services
              </li>
              <li>
                Our Vendors that provide services to enable us to run our
                business and administrative operations, such as legal and
                financial advisory services, auditing services, and other
                similar services
              </li>
              <li>
                Our Vendors that enable us to promote and advertise the
                Services, such as ad platforms or ad retargeting services, as
                well as those that enable us to comply with contact removal
                requests or requirements, such as mailing list removal services,
                do not call registries, and other similar services
              </li>
              <li>
                The Practice, its Practitioners, and pharmacies to enable them
                to provide services to you via the Services and to collect
                payment on their behalf
              </li>
              <li>To researchers, to permit them to study women's health</li>
            </ul>
            <p className="mb-4 text-gray-700">
              We may combine the personal information you share with us through
              the Services with other information we collect from or about you,
              both online and offline. We also may combine personal information
              with records provided by third parties. We use this consolidated
              information to help us better design our Services, including the
              selection of Services and Products, to communicate information to
              you, to enhance our marketing and research activities, and to
              facilitate other business functions. We also may de-identify your
              information and disclose such de-identified information for any
              purpose not prohibited by applicable law.
            </p>
            <p className="mb-4 text-gray-700">
              We may also use your information to contact you about goods and
              services that may be of interest to you, including through
              newsletters. If you wish to opt-out of receiving such
              communications, you may do so at any time by clicking unsubscribe
              at the bottom of these communications.
            </p>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              3.3 Data Retention
            </h3>
            <p className="mb-4 text-gray-700">
              We will retain your personal information for as long as necessary
              to fulfill the purposes for which we have collected it, including
              for your use of our Services and for a reasonable period of time
              afterward. We may also retain your personal information during the
              period of time needed for us to pursue our legitimate business
              interests, conduct audits, comply with our legal obligations,
              resolve disputes, and enforce our agreements.
            </p>
            <p className="mb-4 text-gray-700">
              There may be technical or other operational reasons where we are
              unable to delete your information. Where this is the case, we will
              take reasonable measures to prevent further processing your
              information.
            </p>
          </section>

          {/* Minors Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              4. Minors
            </h2>
            <p className="mb-4 text-gray-700">
              Our Site and Services are not intended for children and
              adolescents under 18 years of age. No one under age 18 may provide
              any information to us through the Site. We do not knowingly
              collect Personally Identifiable Information from individuals under
              18. If you are under 18, do not access, use, or provide any
              information on the Site or on or through any of its features.
            </p>
            <p className="mb-4 text-gray-700">
              If we learn we have collected or received Personally Identifiable
              Information from an individual under 18 without verification of
              parental consent, we will delete that information. If you believe
              we might have any information from or about an individual under
              18, please contact us.
            </p>
          </section>

          {/* Third-Party Sites Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              5. Third-Party Sites
            </h2>
            <p className="mb-4 text-gray-700">
              Our Site may contain links to other third-party sites. When you
              click on one of these links you are visiting a website operated by
              someone other than Elektra Health, and the operator of that
              website may have different privacy policies than we do. Elektra
              Health is not responsible for the individual privacy practices of
              those sites. We encourage you to investigate the privacy policies
              of these third-party operators.
            </p>
            <p className="mb-4 text-gray-700">
              These websites may collect data about you, use cookies, embed
              additional third-party tracking, and monitor your interaction with
              that embedded content, including tracking your interaction with
              the embedded content if you have an account and are logged in to
              that website.
            </p>
          </section>

          {/* Security Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              6. Security
            </h2>
            <p className="mb-4 text-gray-700">
              We may employ procedural and technological security measures,
              consistent with industry practice. Such measures are reasonably
              designed to protect your personal information from loss,
              unauthorized access, disclosure, alteration or destruction. We may
              use encryption, password protection, secure socket layers,
              internal restrictions and other security measures to help prevent
              unauthorized access to your personal information.
            </p>
            <p className="mb-4 text-gray-700">
              However, you provide your information to us at your own risk. We
              cannot guarantee that your data will not be lost, accessed without
              authorization, disclosed, altered, or destroyed.
            </p>
          </section>

          {/* Privacy Notice for Residents Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              7. Privacy Notice for Residents of Connecticut and New Jersey
            </h2>
            <p className="mb-4 text-gray-700">
              This Privacy Policy contains additional information for residents
              of Connecticut and New Jersey about personal data that we collect,
              how we use it, what sources it is derived from, and who we
              disclose it to, and provides information regarding your rights,
              and our responsibilities, under applicable laws and regulations.
              For the purposes of this section, "personal data" means
              information that is linked or reasonably linkable to an identified
              or identifiable individual. Personal data does not include
              de-identified data or publicly available information. This section
              does not apply to certain personal data that is already subject to
              certain federal and state regulations, such as protected health
              information.
            </p>
            <p className="mb-4 text-gray-700">
              The provisions contained within this section are intended to
              provide notices in compliance with the Connecticut Data Privacy
              Act (CTDPA) and the New Jersey Privacy Act (NJPA) (collectively,
              the "State Privacy Laws").
            </p>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              7.1 Our Personal Data Practices
            </h3>
            <p className="mb-4 text-gray-700">
              The State Privacy Laws provide rights to residents of those states
              to receive certain disclosures and access regarding collection,
              use, sale, and sharing of personal data. Detail about what kinds
              of personal data we may collect or have collected, how we collect
              it, why we collect it, and who we may disclose it to is found in
              the "Personal Information We Collect;" "How We Collect Personal
              Information;" and "How We May Use Personal Information" sections
              of this Privacy Policy.
            </p>
            <p className="mb-4 text-gray-700">
              We do not and have not sold Personal Information to third parties
              for any monetary value. We do gather Personal Information via
              Cookies for the purposes of targeted advertising; however, we do
              not sell or share your information without your express,
              affirmative consent.
            </p>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              7.2 Your Privacy Rights
            </h3>
            <p className="mb-4 text-gray-700">
              If you are a resident of Connecticut or New Jersey, you have the
              following rights under applicable law in relation to your personal
              data, subject to certain exceptions:
            </p>
            <ul className="mb-4 ml-6 list-disc text-gray-700">
              <li>
                <strong>Right to know and access.</strong> You have the right to
                know what personal data we collect, use, disclose, and/or sell
                or share as those terms are defined under applicable law. You
                may ask us to provide you a portable copy of this information up
                to two times in a rolling 12-month period.
              </li>
              <li>
                <strong>Right to delete and erase.</strong> You have the right
                to request under certain circumstances that we, as well as our
                service providers and contractors, delete the personal data that
                we collect about you.
              </li>
              <li>
                <strong>Right to correct inaccurate personal data.</strong> You
                have the right to request the correction of inaccurate personal
                data.
              </li>
              <li>
                <strong>Right to non-discrimination.</strong> You have the right
                not to receive discriminatory treatment for the exercise of the
                privacy rights described above.
              </li>
              <li>
                <strong>Right to opt out.</strong> You have the right to opt-out
                of targeted advertising, the sale of your personal data, and
                profiling decisions that could produce legal or similarly
                significant effects concerning the consumer.
              </li>
              <li>
                <strong>Rights concerning sensitive personal data.</strong> If
                you are a Connecticut or New Jersey resident, we cannot and will
                not process your sensitive data (as defined by applicable law)
                or your sensitive data inferences, or use your personal data for
                certain purposes without your affirmative consent.
              </li>
            </ul>
            <p className="mb-4 text-gray-700">
              The CTDPA provides Connecticut residents with specific rights to
              receive certain disclosures and access regarding the collection,
              use, sale, and sharing of Consumer Health Data, as detailed in the{" "}
              <a
                href="/consent/consumer-health-data"
                className="text-blue-600 underline"
              >
                Consumer Health Data Privacy Notice
              </a>
              .
            </p>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              7.3 How to Exercise Your Rights
            </h3>
            <p className="mb-4 text-gray-700">
              You may exercise any of the rights described in this section by
              emailing care@elektrahealth.com.
            </p>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              7.4 How to Appeal Decisions About Your Rights
            </h3>
            <p className="mb-4 text-gray-700">
              Connecticut and New Jersey residents can appeal our decisions
              concerning privacy rights requests, as follows:
            </p>
            <div className="mb-4 space-y-3 text-gray-700">
              <p>
                <strong>a.) Connecticut residents.</strong> If you are a
                Connecticut resident and want to appeal our decision with regard
                to a request that you have made, please Contact Us, as described
                below. Within sixty (60) days of receipt of an appeal, we will
                inform you in writing of any action taken or not taken,
                including an explanation of our reasons in reaching the
                decision. If the appeal is denied, you may contact Connecticut's
                Office of the Attorney General by phone at (860) 808-5420 or by
                submitting a form{" "}
                <a
                  href="https://portal.ct.gov/AG/Common/Complaint-Form-Landing-page"
                  className="text-blue-600 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </p>
              <p>
                <strong>b.) New Jersey residents.</strong> If you are a New
                Jersey resident and want to appeal our decision with regard to a
                request that you have made, please Contact Us, as described
                below (Section 9). Within forty-five (45) days of receipt of an
                appeal, we will inform you in writing of any action taken or not
                taken, including an explanation of our reasons in reaching the
                decision. If the appeal is denied, you may contact New Jersey's
                Office of the Attorney General by phone at (800) 242-5846 or by
                submitting a form{" "}
                <a
                  href="https://www.njconsumeraffairs.gov/Pages/Consumer-Complaints.aspx"
                  className="text-blue-600 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </p>
            </div>
          </section>

          {/* Changes to Privacy Policy Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              8. Changes to this Privacy Policy
            </h2>
            <p className="mb-4 text-gray-700">
              We may change this Privacy Policy from time to time to reflect new
              services or changes in our data practices or relevant laws. We
              will let you know via email and/or a prominent notice on our
              Sites, prior to the change becoming effective, and update
              "effective date" at the top of this Privacy Policy. We recommend
              periodically reviewing this Privacy Policy for any changes;
              changes are effective when they are posted on this page.
            </p>
          </section>

          {/* Contacting Us Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              9. Contacting Us
            </h2>
            <p className="mb-4 text-gray-700">
              To exercise your privacy rights, or if you have any questions
              about this Privacy Policy, email us at care@elektrahealth.com.
            </p>
            <p className="text-sm text-gray-500">
              <strong>Date last modified:</strong> September 15, 2026
            </p>
          </section>
        </div>

        <div className="mt-8 rounded-lg bg-white p-6 text-center">
          <p className="text-sm text-gray-500">
            By using Elektra Health's services, you acknowledge that you have
            read and understood this Privacy Policy and agree to be bound by its
            terms.
          </p>
        </div>
      </div>
    </main>
  );
};
